import { getApi } from "../Services/apiService";
import { getDashboardMemberNameParts } from "./dashboardMemberDisplayName";

/**
 * The "Generations & Ages (All Related)" report — GET /report-management/family-in-age-order.
 *
 * The endpoint does the whole report: living descendants only (no spouses, no
 * deceased), already sorted by generation level and then oldest to youngest,
 * each row carrying its generation, current age, formatted name and family-line
 * colour. Both the dashboard card's grand total and the detail page's list come
 * through here, so the two can never show different numbers.
 *
 * Sample row:
 *   { id, generation_level: 1, age: 85, descendant: "John Michael Doe",
 *     first_name, middle_name, last_name, suffix, special_name,
 *     color: "Blue", hex_code: "#0000FF" }
 */

/** Whole number or null — the report prints "—" for anything it cannot read. */
const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The name in the Desc Name column, spelled the way every other dashboard list
 * spells a descendant: first name, then the suffix after a comma or the special
 * initial after a space — "Ted, Sr.", "Josephine M", "Frank, Jr.".
 *
 * Built from the row's name parts rather than taken from the endpoint's own
 * `descendant` string, which puts the suffix in front ("Sr Ted Witherspoon").
 * That string is the fallback for a row that arrives with no first name.
 */
function buildDescendantName(member) {
  const { baseName, descendantSpouseName } = getDashboardMemberNameParts(
    member,
    { unnamedLabel: "", strictHeadForInLaw: true }
  );

  if (baseName) return { baseName, descendantSpouseName };

  return {
    baseName: String(member?.descendant ?? "").trim(),
    descendantSpouseName: "",
  };
}

/** One API row → the four columns the report prints. */
function toReportRow(member, index) {
  const { baseName, descendantSpouseName } = buildDescendantName(member);

  return {
    id: String(member?.id ?? `desc-${index}`),
    member,
    generation: toNumberOrNull(member?.generation_level),
    age: toNumberOrNull(member?.age),
    baseName,
    descendantSpouseName,
    // What the A-Z sort reads, so it sorts on the name as printed.
    sortName: `${baseName} ${descendantSpouseName}`.trim(),
    colorName: String(member?.color ?? "").trim(),
    hexCode: String(member?.hex_code ?? "").trim() || "#000000",
  };
}

/**
 * Rows plus the header the report prints above them. `totalCount` is the API's
 * own grand total, which is what the heading and the dashboard card show.
 */
export async function fetchFamilyInAgeOrder(familyTreeId) {
  if (!familyTreeId) {
    return { title: "", subtitle: "", year: null, totalCount: 0, rows: [] };
  }

  const response = await getApi(
    `/report-management/family-in-age-order?family_tree_id=${familyTreeId}`
  );

  if (response?.statusCode !== 200 || !response?.data) {
    return { title: "", subtitle: "", year: null, totalCount: 0, rows: [] };
  }

  const members = Array.isArray(response.data.members)
    ? response.data.members
    : [];
  const rows = members.map(toReportRow);

  return {
    title: response.data.title || "",
    subtitle: response.data.subtitle || "",
    year: toNumberOrNull(response.data.year),
    totalCount: toNumberOrNull(response.data.total_count) ?? rows.length,
    rows,
  };
}
